import { useState, useEffect, useRef } from "react";
import "./style.sass";
import assets from "../../assets";
import { QuizComplete } from "./quiz-complete";
import { useHandler } from "./handler";
import { ROUTE_DASHBOARD } from "../../route/communicative";
import { useNavigate } from "react-router-dom";

const COUNT_OPTIONS = [3, 5, 10];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

// ---------------------------------------------------------------------------
// Setup screen (Difficulty + Count + Start Practice)
// ---------------------------------------------------------------------------

const Header = ({ content }) => (
  <header className="voc_header">
    <div className="voc_header_left">
      <img src={assets.ic_logo} alt="Rovian AI" className="logo" />
      <span className="logo_title">Rovian AI</span>
    </div>
    {content}
  </header>
);

function WordCountDropdown({ count, onSelectCount, onSelectManual }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: 1, label: "1 Word" },
    { value: 2, label: "2 Words" },
    { value: 3, label: "3 Words" },
    { value: 4, label: "4 Words" },
    { value: 5, label: "5 Words" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLabel =
    options.find((opt) => opt.value === Number(count))?.label ||
    `${count || 5} Words`;

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-dropdown-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-selected-text">{currentLabel}</span>
        <span className={`dropdown-chevron ${isOpen ? "open" : ""}`}>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="#64748B"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu">
          <div className="dropdown-options-list">
            {options.map((opt) => {
              const isSelected = Number(count) === opt.value;
              return (
                <div
                  key={opt.value}
                  className={`dropdown-option-item ${isSelected ? "selected" : ""}`}
                  onClick={() => {
                    onSelectCount(opt.value);
                    setIsOpen(false);
                  }}
                >
                  <span className="option-label">{opt.label}</span>
                  {isSelected && <span className="option-check">✓</span>}
                </div>
              );
            })}
          </div>

          <div className="dropdown-divider" />

          <div
            className="dropdown-manual-option"
            onClick={() => {
              setIsOpen(false);
              onSelectManual();
            }}
          >
            <span className="manual-icon">✏️</span>
            <span className="manual-label">Enter custom count manually...</span>
          </div>
        </div>
      )}
    </div>
  );
}

function VocabularySetup({ onStartPractice = () => {}, loading }) {
  const [count, setCount] = useState(5);
  const [isManual, setIsManual] = useState(false);
  const [difficulty, setDifficulty] = useState("Beginner");

  const isReady = count && Number(count) > 0 && difficulty;

  const handleStart = () => {
    if (!isReady) return;
    onStartPractice({ count: Number(count), difficulty });
  };
  const navigate = useNavigate();

  return (
    <>
      <Header
        content={
          <div className="back_link" onClick={() => navigate(ROUTE_DASHBOARD)}>
            <img
              src={assets.ic_back_arrow || ""}
              alt=""
              style={{ transform: "rotate(0deg)", width: "12px" }}
            />{" "}
            Dashboard
          </div>
        }
      />
      <div className="vocabulary-setup-page">
        <main className="vocabulary-setup-card">
          <div className="card_header">
            <h1 className="vocabulary-setup__title">Vocabulary Practice</h1>
            <p className="vocabulary-setup__subtitle">
              Select your preferences to learn daily words and test your knowledge with interactive quizzes.
            </p>
          </div>

          <div className="field">
            <label className="field__label">Difficulty</label>
            <div className="difficulty-grid">
              {DIFFICULTIES.map((level) => (
                <button
                  key={level}
                  type="button"
                  className={`difficulty-btn ${difficulty === level ? "active" : ""}`}
                  onClick={() => setDifficulty(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <div className="field-header">
              <label className="field__label">Word Count</label>
              <button
                type="button"
                className="mode-toggle-btn"
                onClick={() => {
                  setIsManual(!isManual);
                  if (isManual && (!count || count < 1 || count > 5)) {
                    setCount(5);
                  }
                }}
              >
                {isManual ? "Switch to Dropdown (1-5)" : "Enter manually instead"}
              </button>
            </div>

            {!isManual ? (
              <WordCountDropdown
                count={count}
                onSelectCount={(val) => setCount(val)}
                onSelectManual={() => {
                  setIsManual(true);
                  setCount("");
                }}
              />
            ) : (
              <div className="manual-count-wrapper">
                <input
                  type="number"
                  min="1"
                  max="50"
                  className="count-manual-input"
                  placeholder="Enter word count (e.g. 7, 10)"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  autoFocus
                />
                <span className="input-suffix">Words</span>
              </div>
            )}
          </div>

          <button
            type="button"
            className={`start-button ${loading ? "loading" : ""}`}
            disabled={!isReady || loading}
            onClick={handleStart}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Loading...
              </>
            ) : (
              "Start Practice"
            )}
          </button>
        </main>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Daily word card
// ---------------------------------------------------------------------------
function DailyWordCard({ word, meaning, example }) {
  const [speaking, setSpeaking] = useState(false);

  const speakWord = (text) => {
    if (!window.speechSynthesis) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.85;

    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  return (
    <section className="word-card">
      <div className="word-card__top">
        <div className="word-card__meta">
          <span className="word-card__chip">Vocabulary</span>
          <h3 className="word-card__title">{word}</h3>
        </div>

        <button
          type="button"
          className={`word-card__speaker ${speaking ? "active" : ""}`}
          onClick={() => speakWord(word)}
          title="Listen to pronunciation"
        >
          <span className="speaker-icon">🔊</span>
          <span className="speaker-text">Pronounce</span>
        </button>
      </div>

      <div className="word-card__body">
        <div className="word-card__meaning-box">
          <span className="row-label">Meaning</span>
          <p className="word-card__meaning">{meaning}</p>
        </div>

        {example && (
          <div className="word-card__example-box">
            <span className="row-label">Example</span>
            <p className="word-card__example">"{example}"</p>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Quiz question
// ---------------------------------------------------------------------------
function QuizQuestion({ index, question, options, selected, onSelect }) {
  const letters = ["A", "B", "C", "D"];
  return (
    <section className={`quiz-card ${selected ? "is-answered" : ""}`}>
      <div className="quiz-card__header">
        <span className="quiz-card__badge">{index + 1}</span>
        <p className="quiz-card__question">{question}</p>
      </div>
      <div className="quiz-card__options">
        {options.map((opt, optIdx) => (
          <button
            type="button"
            key={opt}
            className={
              "quiz-card__option" +
              (selected === opt ? " quiz-card__option--selected" : "")
            }
            onClick={() => onSelect(opt)}
          >
            <span className="option-letter">{letters[optIdx] || optIdx + 1}</span>
            <span className="option-text">{opt}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main practice screen: holds the Daily Words / Vocabulary Quiz tabs
// ---------------------------------------------------------------------------
function VocabularyPractice({
  count,
  difficulty,
  onQuizComplete,
  wordsData,
  quizData,
  submitVocabularyQuiz,
  getDailyWords,
  loading,
  onBackToSetup,
}) {
  const [activeTab, setActiveTab] = useState("daily"); // "daily" | "quiz"
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const activeQuestions = quizData?.questions || [];
  const activeWords = wordsData?.words || [];

  const handleSelect = (questionNo, option) => {
    setAnswers((prev) => ({
      ...prev,
      [questionNo]: option,
    }));
  };

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = activeQuestions?.length || 0;
  const allAnswered = totalQuestions > 0 && answeredCount === totalQuestions;

  const handleSubmit = async () => {
    const answersPayload = Object.entries(answers).map(
      ([questionNo, answer]) => ({
        [questionNo]: answer,
      }),
    );

    const result = await submitVocabularyQuiz({
      quizToken: quizData.quiz_token,
      level: quizData.level,
      answers: answersPayload,
    });

    if (result) {
      onQuizComplete(result.data);
    }
  };

  const handleRefresh = async () => {
    await getDailyWords({
      level: difficulty,
      count,
    });

    setAnswers({});
  };

  return (
    <>
      <Header
        content={
          <div className="back_link" onClick={() => navigate(ROUTE_DASHBOARD)}>
            <img
              src={assets.ic_back_arrow || ""}
              alt=""
              style={{ transform: "rotate(0deg)", width: "12px" }}
            />{" "}
            Dashboard
          </div>
        }
      />

      <main className="vocab-page__content">
        <div className="vocab-page__header-bar">
          <div className="header_text_group">
            <button
              type="button"
              className="vocab_back_btn"
              onClick={onBackToSetup}
            >
              ← Change Setup
            </button>
            <h1 className="vocab-page__title">Vocabulary Practice</h1>
            <p className="vocab-page__subtitle">
              Expand your vocabulary with daily words and test comprehension with quizzes.
            </p>
          </div>
          <div className="vocab_badges">
            <span className="vocab_badge difficulty_badge">{difficulty}</span>
            <span className="vocab_badge count_badge">{count} Words</span>
          </div>
        </div>

        <section className="vocab-page__tabs">
          <div className="tab-buttons-group">
            <button
              type="button"
              className={
                "tab-button" +
                (activeTab === "daily" ? " tab-button--active" : "")
              }
              onClick={() => setActiveTab("daily")}
            >
              <img className="tab-button__icon" src={assets.ic_words} alt="" />
              Daily Words ({activeWords.length})
            </button>
            <button
              type="button"
              className={
                "tab-button" + (activeTab === "quiz" ? " tab-button--active" : "")
              }
              onClick={() => setActiveTab("quiz")}
            >
              <img className="tab-button__icon" src={assets.ic_vocabulary} alt="" />
              Vocabulary Quiz ({activeQuestions.length})
            </button>
          </div>

          {activeTab === "daily" && (
            <button
              type="button"
              className={`vocab-page__refresh ${loading ? "loading" : ""}`}
              disabled={loading}
              onClick={handleRefresh}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <img
                    className="vocab-page__refresh-icon"
                    src={assets.ic_refresh}
                    alt="Refresh"
                  />
                  Refresh Words
                </>
              )}
            </button>
          )}
        </section>

        {activeTab === "daily" && (
          <section className="word-list">
            {activeWords.map((item) => (
              <DailyWordCard key={item.word} {...item} />
            ))}
          </section>
        )}

        {activeTab === "quiz" && (
          <section className="quiz-wrapper">
            <div className="quiz-list">
              {activeQuestions.map((q) => (
                <QuizQuestion
                  key={q.question_no}
                  index={q.question_no - 1}
                  question={q.prompt}
                  options={q.options}
                  selected={answers[q.question_no]}
                  onSelect={(opt) => handleSelect(q.question_no, opt)}
                />
              ))}
            </div>
            <div className="quiz-list__submit-row">
              <button
                type="button"
                className="submit-button"
                onClick={handleSubmit}
                disabled={!allAnswered || loading}
              >
                {loading
                  ? "Submitting..."
                  : allAnswered
                    ? `Submit Quiz (${totalQuestions} Questions)`
                    : `Answer All Questions (${answeredCount}/${totalQuestions})`}
              </button>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

// ---------------------------------------------------------------------------
// Top-level Vocabulary flow controller
// ---------------------------------------------------------------------------
export const Vocabulary = () => {
  // stage: "setup" -> "practice" -> "complete"
  const [stage, setStage] = useState("setup");
  const [quizResult, setQuizResult] = useState(null);
  const [preferences, setPreferences] = useState({
    count: 5,
    difficulty: "Beginner",
  });
  const {
    wordsData,
    setWordsData,
    getVocabularyQuiz,
    quizData,
    setQuizData,
    submitVocabularyQuiz,
    getDailyWords,
    loading,
    setLoading,
  } = useHandler();

  const handleStartPractice = async (prefs) => {
    setPreferences(prefs);

    await getDailyWords({
      level: prefs.difficulty,
      count: prefs.count,
    });

    await getVocabularyQuiz({
      level: prefs.difficulty,
      count: prefs.count,
    });

    setStage("practice");
  };

  const handleQuizComplete = (result) => {
    setQuizResult(result);
    setStage("complete");
  };

  const handleTryAgain = () => {
    setQuizResult(null);
    setWordsData(null);
    setQuizData(null);
    setPreferences({ count: 5, difficulty: "Beginner" });
    setStage("setup");
  };

  return (
    <section className="vocab-page">
      {stage === "setup" && (
        <VocabularySetup
          onStartPractice={handleStartPractice}
          loading={loading}
        />
      )}

      {stage === "practice" && (
        <VocabularyPractice
          count={preferences.count}
          difficulty={preferences.difficulty}
          onQuizComplete={handleQuizComplete}
          wordsData={wordsData}
          quizData={quizData}
          submitVocabularyQuiz={submitVocabularyQuiz}
          getDailyWords={getDailyWords}
          loading={loading}
          onBackToSetup={() => setStage("setup")}
        />
      )}

      {stage === "complete" && (
        <QuizComplete
          correct={quizResult?.correct}
          total={quizResult?.total}
          vocabularyScore={quizResult?.score}
          accuracyScore={quizResult?.score}
          onTryAgain={handleTryAgain}
        />
      )}
    </section>
  );
};
